import { create } from 'zustand';
import { applicationService } from 'src/services/application';
import { UNDER_REVIEW_LS_KEY } from 'src/constants';
import { useApplicationStore } from './application';

const MIN_MODAL_DISPLAY_MS = 2000;

interface UnderReviewState {
  isUnderReview: boolean;
  sessionLockedForHumanReview: boolean;
  applicationState: any;
  firstName: string;
  isModalOpen: boolean;
  modalOpenedAt: number | null;
  // Actions
  checkUnderReviewStatus: () => Promise<void>;
  setSessionLockedForHumanReview: () => void;
  setModalOpen: (isOpen: boolean) => void;
  setDontShowAgain: () => void;
  resetState: () => void;
}

export const useUnderReviewStore = create<UnderReviewState>((set, get) => ({
  isUnderReview: false,
  sessionLockedForHumanReview: false,
  applicationState: null,
  firstName: '',
  isModalOpen: false,
  modalOpenedAt: null,
  setSessionLockedForHumanReview: () => {
    set({ sessionLockedForHumanReview: true });
  },

  syncFromApplicationState: (phqUnderReviewAt: string | null) => {
    const currentLocked = get().sessionLockedForHumanReview;
    if (currentLocked) return;
    const isUnderReview = !!phqUnderReviewAt;
    set({ isUnderReview, sessionLockedForHumanReview: isUnderReview });
  },

  checkUnderReviewStatus: async () => {
    try {
      // Fetch application state to check phqUnderReviewAt
      const applicationState = await applicationService.applicationState();
      
      // Check if user has disabled the modal
      const hideModal = typeof window !== 'undefined' && localStorage.getItem(UNDER_REVIEW_LS_KEY) === '1';
      
      const phqFromApi = applicationState?.phqUnderReviewAt;
      const phqFromStore = useApplicationStore.getState().phqUnderReviewAt;
      const isUnderReview = !!(phqFromApi || phqFromStore);

      useApplicationStore.setState({ phqUnderReviewAt: phqFromApi ?? phqFromStore });

      const sessionLockedForHumanReview = isUnderReview || get().sessionLockedForHumanReview;

      const shouldShowModal = isUnderReview && !hideModal;
      set({ applicationState, isUnderReview, sessionLockedForHumanReview });
      if (shouldShowModal) {
        set({ isModalOpen: true, modalOpenedAt: Date.now() });
      } else {
        get().setModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to check under review status:', error);
      const phqFromStore = useApplicationStore.getState().phqUnderReviewAt;
      const keepOpen = !!phqFromStore;
      if (keepOpen) {
        set({ isUnderReview: true });
      } else {
        set({ isUnderReview: false });
        get().setModalOpen(false);
      }
    }
  },

  setModalOpen: (isOpen: boolean) => {
    if (isOpen) {
      set({ isModalOpen: true, modalOpenedAt: Date.now() });
    } else {
      const { modalOpenedAt } = get();
      const elapsed = modalOpenedAt ? Date.now() - modalOpenedAt : 0;
      const remaining = Math.max(0, MIN_MODAL_DISPLAY_MS - elapsed);
      if (remaining > 0) {
        setTimeout(() => set({ isModalOpen: false }), remaining);
      } else {
        set({ isModalOpen: false });
      }
    }
  },

  setDontShowAgain: () => {
    try {
      localStorage.setItem(UNDER_REVIEW_LS_KEY, '1');
    } catch (error) {
      console.error('Failed to save dont show again preference:', error);
    }
    get().setModalOpen(false);
  },

  resetState: () => {
    set({
      isUnderReview: false,
      sessionLockedForHumanReview: false,
      applicationState: null,
      firstName: '',
      isModalOpen: false,
      modalOpenedAt: null,
    });
  },
}));
